import { Autocomplete, Box, Button, Chip, TextField } from "@mui/material";
import { orderBy } from "lodash-es";
import { type FC, useCallback, useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type { UUID } from "../../../utils/uuid";
import { SupabaseConnectionKeysService } from "../../merge-tag/server/supabase-connections-key-service";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { SupabaseRecipientService } from "../../recipients/server/supabase-recipient-service";

type Choices = (
  | {
      type: "Subject Connections";
      id: string;
      display: string;
    }
  | {
      type: "People";
      id: UUID;
      display: string | null;
      destination: string;
    }
)[];

export const DynamicCCForm: FC<{ disabled: boolean; initiallyOpen?: boolean }> = ({
  disabled,
  initiallyOpen = false,
}) => {
  const choiceLoader = useSupabaseLoader(async ({ supabase, account_id, abortController }) => {
    const [{ data: recipients, error: recipientsErr }, categories] = await Promise.all([
      new SupabaseRecipientService(supabase).getForChannel(account_id, "work_email"),
      new SupabaseConnectionKeysService(supabase).getCategories(),
    ]);

    if (recipientsErr) {
      if (!abortController?.signal?.aborted)
        console.error("Error loading CC/BCC choices", JSON.stringify(recipientsErr));
      throw recipientsErr;
    }

    return orderBy(
      [
        ...categories.map((category) => ({
          type: "Subject Connections" as const,
          id: category.key,
          display: category.display_name,
        })),
        ...recipients.map((recipient) => ({
          type: "People" as const,
          id: recipient.id,
          display: recipient.display,
          destination: recipient.destination,
        })),
      ],
      ["type", "display"],
      ["desc", "asc"],
    );
  }, []);

  return (
    <>
      <Selector
        type="cc"
        disabled={disabled}
        initiallyOpen={initiallyOpen}
        choiceLoader={choiceLoader}
      />
      <Selector
        type="bcc"
        disabled={disabled}
        initiallyOpen={initiallyOpen}
        choiceLoader={choiceLoader}
      />
    </>
  );
};

const Selector: FC<{
  type: "cc" | "bcc";
  disabled: boolean;
  initiallyOpen: boolean;
  choiceLoader: UseSupabaseLoaderState<Choices>;
}> = ({ type, disabled, initiallyOpen, choiceLoader: { data: choices, loading } }) => {
  const [show, setShow] = useState(initiallyOpen);

  const { control, getValues, setValue } = useFormContext<MomentFormMoment>();

  useWatch({ control, name: ["cc_bcc"] });
  const cc_bcc = getValues("cc_bcc");

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if ((cc_bcc?.[type] ?? []).length > 0) setShow(true);
  }, [cc_bcc, type]);

  const onChange = useCallback(
    (value: NonNullable<MomentFormMoment["cc_bcc"]>["cc"]) => {
      const currentValue = getValues("cc_bcc") ?? { cc: [], bcc: [] };

      setValue("cc_bcc", {
        ...currentValue,
        [type]: value,
      });
    },
    [getValues, setValue, type],
  );

  return (
    <Box>
      {!show && (
        <Button
          data-analytics-id="moment-form-cc-button"
          sx={{ mt: 2 }}
          onClick={() => setShow(true)}
          disabled={disabled}
          loading={loading}
        >
          {type.toLocaleUpperCase()}
        </Button>
      )}
      {show && (
        <Controller
          name="cc_bcc"
          control={control}
          render={({ field }) => (
            <Autocomplete
              id={`${type}-select`}
              multiple
              value={(choices ?? []).filter((x) => {
                const values = field.value?.[type] ?? [];
                if (x.type === "Subject Connections") {
                  return values.some((y) => y.type === "dynamic" && y.connection === x.id);
                }
                return values.some((y) => y.type === "static" && y.id === x.id);
              })}
              options={choices ?? []}
              groupBy={(option) => option.type}
              disabled={disabled}
              loading={loading}
              renderOption={(renderProps, option) => (
                <Box component="li" {...renderProps} key={option.id}>
                  {option.type === "Subject Connections"
                    ? option.display
                    : option.display
                      ? `${option.display} <${option.destination}>`
                      : option.destination}
                </Box>
              )}
              renderValue={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option.id} label={option.display} />
                ))
              }
              renderInput={(params) => <TextField label={type} variant="standard" {...params} />}
              getOptionLabel={(option) =>
                option.type === "Subject Connections"
                  ? option.display
                  : option.display || option.destination
              }
              getOptionKey={(option) => option.id}
              onChange={(_, value, reason) => {
                if (reason === "clear" || value.length === 0) onChange([]);
                else
                  onChange(
                    value.map((x) => {
                      if (x.type === "Subject Connections") {
                        return { type: "dynamic", connection: x.id };
                      }
                      return { type: "static", id: x.id };
                    }),
                  );
              }}
            />
          )}
        />
      )}
    </Box>
  );
};
